/**
 * Status language — ONE system across dashboard + session + widgets.
 * Ported from design/project/lib/ns-kit.jsx: tone → token color; each
 * status maps to a tone + whether it pulses. This registry replaces
 * ad-hoc status dots/classes everywhere.
 */
import type { CSSProperties, ReactNode } from "react";

export type Tone = "live" | "idle" | "pending" | "warn" | "danger" | "off";

export interface ToneSpec {
  /** Solid color (dot, text). */
  c: string;
  /** Tint fill. */
  soft: string;
  /** Tinted border. */
  line: string;
}

export const TONE: Record<Tone, ToneSpec> = {
  live: {
    c: "var(--color-success)",
    soft: "var(--color-success-soft)",
    line: "rgba(61,207,142,0.32)",
  },
  idle: {
    c: "var(--color-fg-3)",
    soft: "rgba(136,146,160,0.10)",
    line: "var(--color-line-2)",
  },
  pending: {
    c: "var(--color-accent)",
    soft: "var(--color-accent-soft)",
    line: "var(--color-accent-line)",
  },
  warn: {
    c: "var(--color-warn)",
    soft: "var(--color-warn-soft)",
    line: "rgba(233,185,73,0.32)",
  },
  danger: {
    c: "var(--color-danger)",
    soft: "var(--color-danger-soft)",
    line: "rgba(242,109,109,0.32)",
  },
  off: {
    c: "var(--color-fg-4)",
    soft: "rgba(93,102,116,0.10)",
    line: "var(--color-line-1)",
  },
};

export interface StatusSpec {
  tone: Tone;
  label: string;
  pulse: boolean;
}

/** Canonical status registry: machine / session / job / schedule. */
export const STATUS: Record<string, StatusSpec> = {
  // machines
  online: { tone: "live", label: "online", pulse: true },
  offline: { tone: "off", label: "offline", pulse: false },
  // sessions / agents
  working: { tone: "live", label: "working", pulse: true },
  streaming: { tone: "live", label: "live", pulse: true },
  idleS: { tone: "idle", label: "idle", pulse: false },
  ended: { tone: "off", label: "ended", pulse: false },
  // jobs
  queued: { tone: "pending", label: "queued", pulse: false },
  running: { tone: "live", label: "running", pulse: true },
  delivered: { tone: "live", label: "delivered", pulse: false },
  failed: { tone: "danger", label: "failed", pulse: false },
  skipped: { tone: "idle", label: "skipped", pulse: false },
  // schedules
  enabled: { tone: "live", label: "enabled", pulse: false },
  paused: { tone: "warn", label: "paused", pulse: false },
  // phase 9: dispatch-gate holds (queued jobs) + seat/budget flags
  "hold-budget": { tone: "warn", label: "waiting: budget", pulse: false },
  "hold-seat-limit": { tone: "warn", label: "waiting: seat limit", pulse: false },
  "seat-limited": { tone: "warn", label: "seat limited", pulse: false },
  "over-budget": { tone: "warn", label: "over budget", pulse: false },
};

/** Tone colors handed to the .ns-status-* classes as CSS vars. */
function toneVars(t: ToneSpec): CSSProperties {
  return {
    "--ns-tone": t.c,
    "--ns-tone-soft": t.soft,
    "--ns-tone-line": t.line,
  } as CSSProperties;
}

export function StatusDot({
  tone = "idle",
  pulse = false,
  size = 7,
}: {
  tone?: Tone;
  pulse?: boolean;
  size?: number;
}) {
  const t = TONE[tone] ?? TONE.idle;
  return (
    <span
      className="ns-status-dot"
      style={{ width: size, height: size, ...toneVars(t) }}
    >
      {pulse && <span className="ns-status-dot-ping" />}
      <span className="ns-status-dot-core" />
    </span>
  );
}

export function StatusPill({
  status,
  children,
  mono = true,
}: {
  status: string;
  children?: ReactNode;
  mono?: boolean;
}) {
  const s = STATUS[status] ?? { tone: "idle" as Tone, label: status, pulse: false };
  const t = TONE[s.tone];
  return (
    <span
      className={"ns-status-pill" + (mono ? "" : " ns-status-pill--sans")}
      style={toneVars(t)}
    >
      <StatusDot tone={s.tone} pulse={s.pulse} size={6} />
      {children ?? s.label}
    </span>
  );
}

export type ChipTone = "neutral" | "accent" | "ghost";

export function MonoChip({
  children,
  tone = "neutral",
  style,
}: {
  children: ReactNode;
  tone?: ChipTone;
  style?: CSSProperties;
}) {
  const mod =
    tone === "accent"
      ? " ns-mono-chip--accent"
      : tone === "ghost"
        ? " ns-mono-chip--ghost"
        : "";
  return (
    <span className={"ns-mono-chip" + mod} style={style}>
      {children}
    </span>
  );
}
